/**
 * Wraps fetch with an AbortController-based timeout.
 * If `signal` is provided in init, it is merged with the timeout signal.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number }
): Promise<Response> {
  const { timeoutMs = 30_000, ...fetchInit } = init ?? {};

  const controller = new AbortController();
  const externalSignal = fetchInit.signal;

  // If caller already provides a signal, abort our controller when that fires
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), {
        once: true,
      });
    }
  }

  const timer = setTimeout(() => controller.abort(new Error('Request timed out')), timeoutMs);

  try {
    return await fetch(input, { ...fetchInit, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
